/* @flow */
import cx from 'classnames';
import * as React from 'react';
import Time from 'components/Time.jsx';
import Link from 'components/Link.jsx';
import Glyph from 'components/Glyph.jsx';
import CurrentUser from 'auth/CurrentUser';
import ChatMessage from 'entities/ChatMessage';
import MessageItemContent from './MessageItemContent.jsx';
import { ReactionContainer } from 'chat/components/Reaction';
import ChatMessageActions from '../actions/ChatMessageActions';
import ReactionButton from 'chat/components/ReactionButton.jsx';
import ChatReadReceipt from 'chat/components/ChatReadReceipt.jsx';
import ReactionDisplay from 'chat/components/ReactionDisplay.jsx';
import ReactionPickerTrigger from 'chat/components/ReactionPickerTrigger.jsx';
import MessageSenderAvatarContainer from 'chat/components/MessageSenderAvatarContainer.jsx';

import './styles/MessageItem.scss';

type Props = {
  failed?: boolean,
  message: ChatMessage,
  sentBySelf?: boolean,
  lastOfGroup?: boolean,
};

type State = {
  hovered: boolean,
  reactionPickerVisible: boolean,
};

class MessageItem extends React.PureComponent<Props, State> {
  state: State = {
    hovered: false,
    reactionPickerVisible: false,
  };

  handleCancel = () => {
    ChatMessageActions.removeMessage(this.props.message);
  };

  handleRetry = () => {
    ChatMessageActions.sendMessage(this.props.message);
  };

  showReadReceipt(message: ChatMessage) {
    return message.getSender().getUuid() === CurrentUser.getUserUuid();
  }

  handleHoverReactionPickerShow = () => {
    this.setState({ reactionPickerVisible: true });
  };

  handleHoverReactionPickerHide = () => {
    this.setState({ reactionPickerVisible: false });
  };

  handleEnterHover = () => {
    this.setState({ hovered: true });
  };

  handleLeaveHover = () => {
    this.setState({ hovered: false });
  };

  renderReadReceipt(message: ChatMessage) {
    return (
      this.showReadReceipt(message) && (
        <ReactionContainer chatUuid={message.getChatId()} messageUuid={message.id}>
          {({ reactionSummary }) => {
            if (reactionSummary && reactionSummary.reactions.length > 0) {
              return null;
            }

            return (
              <div className="MessageItem-readIcon">
                <ChatReadReceipt hovered labelDisplay glyphDisplay message={message} />
              </div>
            );
          }}
        </ReactionContainer>
      )
    );
  }

  renderReactions() {
    const { message, sentBySelf } = this.props;

    return (
      <ReactionContainer chatUuid={message.getChatId()} messageUuid={message.id}>
        {({ reactionSummary, addReactionToSummary, removeReactionFromSummary }) => {
          if (!reactionSummary) {
            return null;
          }

          return (
            <ReactionDisplay
              sentBySelf={sentBySelf}
              reactionSummary={reactionSummary}
              addReactionToSummary={addReactionToSummary}
              removeReactionFromSummary={removeReactionFromSummary}
            />
          );
        }}
      </ReactionContainer>
    );
  }

  renderRetry() {
    return (
      <div className="MessageItem-retry">
        <Glyph name="caution" className="MessageItem-retryGlyph" />
        Message failed to send.{' '}
        <Link weight="semibold" onClick={this.handleRetry}>
          Retry
        </Link>{' '}
        or{' '}
        <Link weight="semibold" onClick={this.handleCancel}>
          Cancel
        </Link>
      </div>
    );
  }

  renderHover() {
    const { message, sentBySelf } = this.props;

    return (
      <div className="MessageItem-hover">
        <div>
          <Time date={message.getSentAt()} hour="numeric" minute="2-digit" />
          {sentBySelf && (
            <ChatReadReceipt hovered labelDisplay={false} glyphDisplay message={message} />
          )}
        </div>
        <div className="MessageItem-hover-reactionButton">
          {message.canReact() && (
            <ReactionContainer chatUuid={message.getChatId()} messageUuid={message.id}>
              {({ reactionSummary, addReactionToSummary, removeReactionFromSummary }) => {
                if (reactionSummary && reactionSummary.reactions.length >= 1) {
                  return null;
                }

                return (
                  <ReactionPickerTrigger
                    onShow={this.handleHoverReactionPickerShow}
                    onHide={this.handleHoverReactionPickerHide}
                    alignment={sentBySelf ? 'left' : 'right'}
                    addReactionToSummary={addReactionToSummary}
                    removeReactionFromSummary={removeReactionFromSummary}>
                    <ReactionButton />
                  </ReactionPickerTrigger>
                );
              }}
            </ReactionContainer>
          )}
        </div>
      </div>
    );
  }

  renderDroplet() {
    const { lastOfGroup, message, sentBySelf } = this.props;
    const senderUuid = message.getSender().getUuid();

    if (!lastOfGroup || sentBySelf) {
      return null;
    }

    return (
      <span className="MessageItem-droplet">
        <MessageSenderAvatarContainer uuid={senderUuid} />
      </span>
    );
  }

  render() {
    const { message, failed, sentBySelf } = this.props;
    const { hovered, reactionPickerVisible } = this.state;

    return (
      <div
        className={cx('MessageItem', {
          'MessageItem-self': sentBySelf,
          'MessageItem-other': !sentBySelf,
          'MessageItem-hovered': hovered || reactionPickerVisible,
        })}>
        <div
          className="MessageItem-row"
          onMouseEnter={this.handleEnterHover}
          onMouseLeave={this.handleLeaveHover}>
          {this.renderHover()}
          <div className="MessageItem-container">
            <MessageItemContent message={message} failed={failed} sentBySelf={sentBySelf} />
            {failed && this.renderRetry()}
          </div>
          {this.renderDroplet()}
        </div>
        {message.canReact() && this.renderReactions()}
        {this.renderReadReceipt(message)}
      </div>
    );
  }
}

export { MessageItem };
export default MessageItem;
